/**
 * My Pipeline — the rep's weekly forecast pass (product brief §5, journey J3).
 *
 * The acceptance test is not "does it render" but "does a weekly pass over a
 * rep's clients take minutes". Everything is arranged around that: one linear
 * column, flagged clients first, every lever one gesture from the composition,
 * no navigation away to act, and a keyboard path for the rep who does this
 * every week and should not reach for a mouse twelve times.
 *
 * Four things carry more weight than they look:
 *
 * - **Every lever previews its effect** before it is pulled, and the pass keeps
 *   a running total. A rep who cannot see what a lever does learns not to touch
 *   it, and hygiene work that shows no result is indistinguishable from admin.
 * - **The material-move threshold is visible.** A 10% manual move notifies the
 *   sales manager; a rep who discovers that after the fact stops using the page.
 *   A control the controlled person cannot see is a trap.
 * - **Everything is undoable.** Interventions apply immediately with no approval
 *   step (brief §6, bounded authority), so undo is what makes immediacy safe.
 * - **Next month is on screen.** Pushing a quote's expected date moves value out
 *   of this month; without somewhere for it to land, the tool looks like it is
 *   hiding money.
 *
 * Money on this page is the slice landing IN THIS MONTH — what the month-landing
 * chart counts — because reconciling to that chart is how the tool earns trust.
 *
 * Interventions apply through the pure engine as an optimistic update, then go
 * to the owning module's routes and are confirmed by a refetch. The engine is
 * what makes the wait invisible; the server is the truth. Passing a `repId`
 * serves a report's book instead of your own — the server decides whether you
 * may ask.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  ChevronLeft,
  ChevronRight,
  Inbox,
  CalendarClock,
  ListFilter,
  Search,
  Sparkles,
  Undo2,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { paths } from "@shared-core/routing/routes";
import type {
  MyPipelineResponse,
  PipelineClient,
  PipelineQuote,
} from "@shared-core/data/my-pipeline-types";
import {
  formatCurrencyGbpCompact,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import {
  applyIntervention,
  describeMarkLostFailures,
  describeNoteFailures,
  measureMove,
  previewImpact,
  MATERIAL_MOVE_PCT,
  type Actor,
  type Intervention,
  type InterventionImpact,
  type InterventionTarget,
  resolveQuoteTargets,
} from "@shared-core/domain/my-pipeline-interventions";
import { Badge } from "../app/components/primitives/badge";
import { previousMonthKey } from "@shared-core/domain/month-keys";
import { Button } from "../app/components/primitives/button";
import { Card } from "../app/components/primitives/card";
import { Input } from "../app/components/primitives/input";
import { isOverSixMonthsAndWrittenOff } from "@shared-core/domain/my-pipeline-quote-age";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { useAuth } from "../app/auth/hooks";
import { WorkspaceNav } from "../app/components/domain/forecasting/WorkspaceNav";
import { ProvenanceLegend } from "../app/components/patterns/ProvenanceValue";
import { ClientCompositionCard } from "../app/components/domain/forecasting/pipeline/ClientCompositionCard";
import { AllQuotesTable } from "../app/components/domain/forecasting/pipeline/AllQuotesTable";
import {
  InterventionDialog,
  type InterventionRequest,
  type InterventionResult,
} from "../app/components/domain/forecasting/pipeline/InterventionDialog";
import { currentTime } from "@shared-core/domain/current-time";

type ViewMode = "by_client" | "all_quotes";

/** One reversible step of the pass. */
interface LogEntry {
  id: number;
  label: string;
  /** The whole book before this step, for the rollback on a failed write. */
  before: MyPipelineResponse;
  /** Whether the server can actually reverse this — see `isReversible`. */
  reversible: boolean;
  /** What it was done to, so undo can address the same rows. */
  targets: InterventionTarget[];
}

/**
 * Can this gesture be taken back?
 *
 * Only the two overrides: `reset-override` drops a quote's conversion and date
 * back to the algorithm. Marking lost writes a status into Tharstern, which has
 * no un-set-status call, and a review is a recorded fact rather than a value.
 * Offering undo on those would restore the page and nothing else.
 */
function isReversible(intervention: Intervention): boolean {
  return (
    intervention.kind === "set_conversion" ||
    intervention.kind === "set_expected_date"
  );
}

/**
 * Clients with the most to decide come first, then the largest projection. A
 * rep working strictly top-down hits the highest-value uncertainty first and
 * reaches the clean clients last.
 */
function orderClients(clients: PipelineClient[]): PipelineClient[] {
  return [...clients].sort((a, b) => {
    if (a.flag_count !== b.flag_count) return b.flag_count - a.flag_count;
    return b.projected_unbooked - a.projected_unbooked;
  });
}

/** Map the dialog's result onto the engine's intervention vocabulary. */
function toIntervention(result: InterventionResult): Intervention | null {
  const { lever, value, date, reasonCode, note } = result;
  switch (lever.kind) {
    case "conversion":
      return value == null
        ? null
        : { kind: "set_conversion", share: value, note };
    case "unseen":
      return value == null ? null : { kind: "set_unseen", value, note };
    case "expected_date":
      return date == null ? null : { kind: "set_expected_date", date, note };
    case "status":
      return lever.target === "still_live"
        ? { kind: "confirm_still_live", note }
        : { kind: "mark_lost", reasonCode: reasonCode ?? "Lost", note };
  }
}

function describe(result: InterventionResult, count: number): string {
  const { lever, value, date, reasonCode, note } = result;
  const scope = count > 1 ? `${count} quotes · ` : "";
  const suffix = note ? ` — "${note}"` : "";
  switch (lever.kind) {
    case "conversion":
      return `${scope}Conversion set to ${Math.round((value ?? 0) * 100)}%${suffix}`;
    case "unseen":
      return `${scope}Unseen work set to ${formatCurrencyGbpCompact(value ?? 0)}${suffix}`;
    case "expected_date":
      return `${scope}Expected ${date ? formatShortDateOrDash(date) : "—"}${suffix}`;
    case "status":
      return lever.target === "still_live"
        ? `${scope}Confirmed still live${suffix}`
        : `${scope}Marked lost · ${reasonCode}${suffix}`;
  }
}

export function MyPipelinePage() {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  // Absent on `/plan/my-pipeline` (your own book) and present when a manager
  // opens a report's. Passed straight through: the server decides who may be
  // asked for, so this is a request, not an authority.
  const { repId } = useParams<{ repId?: string }>();
  const navigate = useNavigate();
  // The month on the URL, so a stepped-to month survives a refresh and can be
  // sent as a link. Absent means the current month; the server decides which.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedMonth = searchParams.get("month") ?? undefined;
  const stepToMonth = useCallback(
    (monthKey: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("month", monthKey);
        return next;
      });
    },
    [setSearchParams],
  );
  const { user: currentUser } = useAuth();
  const [data, setData] = useState<MyPipelineResponse | null>(null);
  /** The book as loaded — the denominator for the pass's running total. */
  const [baseline, setBaseline] = useState<MyPipelineResponse | null>(null);
  const [lostReasons, setLostReasons] = useState<readonly string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<ViewMode>("by_client");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [query, setQuery] = useState("");
  // Default ON. Three reps asked in week one to stop seeing quotes that are
  // past their curve and long since quoted; the cut is six months, chosen by
  // the business off the live age profile (see the predicate's own docstring,
  // which records why it is not a year any more). Hiding is presentation — the
  // forecast is untouched.
  const [hideLongDead, setHideLongDead] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<InterventionRequest | null>(null);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [focusIndex, setFocusIndex] = useState(-1);
  const [leverError, setLeverError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const logId = useRef(0);

  // The unseen-work route keys on a month start; the response carries the
  // month as a `YYYY-MM` key.
  const monthStart = data ? `${data.month}-01` : "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [pipeline, reasons] = await Promise.all([
          adapter.getMyPipeline(repId, requestedMonth),
          adapter.getEstimateLostReasons(),
        ]);
        if (cancelled) return;
        setData(pipeline);
        setBaseline(pipeline);
        setLostReasons(reasons);
        const first = orderClients(pipeline.clients).find(
          (c) => c.flag_count > 0,
        );
        if (first) setExpanded(new Set([first.id]));
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Could not load your pipeline",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter, repId, requestedMonth]);

  const repName = data?.rep_name;
  const asOfDate = data?.as_of_date;
  const monthLabel = data?.month_label;

  const actor: Actor = useMemo(
    () => ({
      name: repName ?? "You",
      today: asOfDate ?? currentTime().toISOString().slice(0, 10),
    }),
    [repName, asOfDate],
  );

  const ordered = useMemo(
    () => (data ? orderClients(data.clients) : []),
    [data],
  );

  const matchesQuery = useCallback(
    (quote: PipelineQuote, clientName: string) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return (
        quote.reference.toLowerCase().includes(q) ||
        quote.description.toLowerCase().includes(q) ||
        clientName.toLowerCase().includes(q)
      );
    },
    [query],
  );

  const visibleClients = useMemo(
    () => (flaggedOnly ? ordered.filter((c) => c.flag_count > 0) : ordered),
    [ordered, flaggedOnly],
  );

  /**
   * The flat list the keyboard walks, in the order the eye reads. Only expanded
   * clients contribute in the grouped view — a shortcut that acts on something
   * the rep cannot see is worse than no shortcut.
   */
  const navigable = useMemo(() => {
    const rows: { client: PipelineClient; quote: PipelineQuote }[] = [];
    if (!data) return rows;
    if (view === "by_client") {
      for (const client of visibleClients) {
        if (!expanded.has(client.id)) continue;
        for (const quote of client.quotes) {
          if (hideLongDead && isOverSixMonthsAndWrittenOff(quote)) continue;
          if (matchesQuery(quote, client.name)) rows.push({ client, quote });
        }
      }
    } else {
      for (const client of ordered) {
        for (const quote of client.quotes) {
          if (flaggedOnly && quote.flags.length === 0) continue;
          if (hideLongDead && isOverSixMonthsAndWrittenOff(quote)) continue;
          if (matchesQuery(quote, client.name)) rows.push({ client, quote });
        }
      }
    }
    return rows;
  }, [
    data,
    view,
    visibleClients,
    ordered,
    expanded,
    flaggedOnly,
    hideLongDead,
    matchesQuery,
  ]);

  /**
   * Send one intervention to the owning module's routes.
   *
   * `mark_lost` goes as a single bulk call because the route is bulk and a
   * per-quote loop would write back to Tharstern N times for one gesture.
   * Everything else is per target.
   */
  const dispatch = useCallback(
    async (targets: InterventionTarget[], intervention: Intervention) => {
      const quoteIds = targets
        .filter((t) => t.kind === "quote")
        .map((t) => t.quoteId);

      switch (intervention.kind) {
        case "mark_lost": {
          const result = await adapter.markQuotesLost(
            quoteIds,
            intervention.reasonCode,
            intervention.note,
          );
          // Partial success is normal on this route; a refused write must
          // surface as "Not saved", not vanish into an optimistic update.
          const failure = describeMarkLostFailures(result.outcomes);
          if (failure) throw new Error(failure);
          // A note that did not reach Tharstern is a warning, not a rollback:
          // the status landed and the quote is decided either way.
          const noteFailure = describeNoteFailures(result.outcomes);
          if (noteFailure) setLeverError(noteFailure);
          return;
        }
        case "confirm_still_live":
          await Promise.all(
            quoteIds.map((id) =>
              adapter.markQuoteReviewed(id, intervention.note),
            ),
          );
          return;
        case "set_conversion":
          await Promise.all(
            quoteIds.map((id) =>
              adapter.setQuoteConversion(
                id,
                intervention.share,
                intervention.note,
              ),
            ),
          );
          return;
        case "set_expected_date":
          await Promise.all(
            quoteIds.map((id) =>
              adapter.setQuoteExpectedDate(
                id,
                intervention.date,
                intervention.note,
              ),
            ),
          );
          return;
        case "revert_conversion":
        case "revert_expected_date":
          await Promise.all(
            quoteIds.map((id) => adapter.resetQuoteToAlgorithm(id)),
          );
          return;
        case "set_unseen": {
          const clients = targets.filter((t) => t.kind === "client");
          await Promise.all(
            clients.map((t) =>
              adapter.setUnseenWork(
                t.clientId,
                monthStart,
                intervention.value,
                intervention.note,
              ),
            ),
          );
          return;
        }
        default:
          // `revert_unseen` and `resolve_arrival` have no lever on this page —
          // arrivals are settled on their own queue. Nothing to send.
          return;
      }
    },
    [adapter, monthStart],
  );

  /**
   * Apply a lever: optimistic update, then the real write, then a refetch.
   *
   * The optimistic step is what makes a pass feel instant; the refetch is what
   * makes it true. Both matter — the engine alone is how this page originally
   * shipped, looking like it worked while persisting nothing.
   *
   * A failed write rolls the book back to what it was before the gesture. It
   * does not roll back Tharstern: `mark_lost` writes the status there and
   * Tharstern has no un-set-status call, so a partial batch stays partial and
   * the refetch is what shows the rep the truth.
   */
  const commit = useCallback(
    (
      targets: InterventionTarget[],
      intervention: Intervention,
      label: string,
    ) => {
      const before = data;
      if (!before) return;
      const next = targets.reduce(
        (acc, t) => applyIntervention(acc, t, intervention, actor),
        before,
      );
      setData(next);
      setSelected(new Set());
      setLeverError(null);
      setSaving(true);
      void (async () => {
        try {
          await dispatch(targets, intervention);
          const fresh = await adapter.getMyPipeline(repId, requestedMonth);
          setData(fresh);
          setLog((prev) => [
            {
              id: (logId.current += 1),
              label,
              before,
              reversible: isReversible(intervention),
              targets,
            },
            ...prev,
          ]);
        } catch (err) {
          setData(before);
          setLeverError(
            err instanceof Error
              ? err.message
              : "That change could not be saved.",
          );
        } finally {
          setSaving(false);
        }
      })();
    },
    [actor, adapter, data, dispatch, repId, requestedMonth],
  );

  /**
   * Undo the last gesture — for the levers that have a real reversal.
   *
   * Only the conversion and date overrides do: `reset-override` drops them
   * server-side. A lost quote cannot be un-lost (Tharstern has no such call),
   * a review cannot be un-reviewed, so those entries are recorded but offer no
   * undo. Restoring the page's own state and calling that "undone" would be a
   * lie the next refetch exposes.
   */
  const undo = useCallback(() => {
    const last = log[0];
    if (!last?.reversible) return;
    setLog((prev) => prev.slice(1));
    setLeverError(null);
    setSaving(true);
    void (async () => {
      try {
        await Promise.all(
          last.targets
            .filter((t) => t.kind === "quote")
            .map((t) => adapter.resetQuoteToAlgorithm(t.quoteId)),
        );
        setData(await adapter.getMyPipeline(repId, requestedMonth));
      } catch (err) {
        setLeverError(
          err instanceof Error ? err.message : "That undo could not be saved.",
        );
      } finally {
        setSaving(false);
      }
    })();
  }, [adapter, log, repId, requestedMonth]);

  /** Resolve the request the dialog is showing back to engine targets. */
  const targetsFor = useCallback(
    (request: InterventionRequest): InterventionTarget[] => {
      if (!data) return [];
      if (request.lever.kind === "unseen") {
        const client = data.clients.find(
          (c) => c.name === request.subjectLabel,
        );
        return client ? [{ kind: "client", clientId: client.id }] : [];
      }
      // `bulkCount` being present is what marks a request as coming from the
      // selection bar, not its size. Reading it as `> 1` meant one ticked box
      // fell through to `request.quoteId` — which a bulk request never
      // carries — and resolved to no targets at all, so the gesture wrote
      // nothing. That is the "multi select lost not working" reps reported:
      // the first thing anyone does with a new control is try it on one row.
      const isBulk = request.bulkCount != null && selected.size > 0;
      return resolveQuoteTargets(data.clients, {
        quoteId: request.quoteId,
        selectedIds: isBulk ? selected : undefined,
      });
    },
    [data, selected],
  );

  const previewFor = useCallback(
    (draft: InterventionResult): InterventionImpact | null => {
      if (!pending || !data) return null;
      const intervention = toIntervention(draft);
      if (!intervention) return null;
      const targets = targetsFor(pending);
      if (targets.length === 0) return null;
      if (targets.length === 1) {
        return previewImpact(data, targets[0], intervention, actor);
      }
      const after = targets.reduce(
        (acc, t) => applyIntervention(acc, t, intervention, actor),
        data,
      );
      return measureMove(data.projected_unbooked, after.projected_unbooked);
    },
    [pending, data, targetsFor, actor],
  );

  const handleApply = useCallback(
    (result: InterventionResult) => {
      if (!pending || !data) return;
      const intervention = toIntervention(result);
      if (!intervention) return;
      const targets = targetsFor(pending);
      if (targets.length === 0) {
        // The dialog has already accepted the gesture and is about to close.
        // Returning quietly here is how a bulk clear looked like it worked and
        // wrote nothing for a week: say so instead, so the next one is a bug
        // report and not a rep quietly deciding the page lies.
        setLeverError(
          "Nothing was saved — that selection resolved to no quotes. Reload the page and try again.",
        );
        return;
      }
      commit(targets, intervention, describe(result, targets.length));
    },
    [pending, data, targetsFor, commit],
  );

  // Keyboard: j/k walks the visible quotes, then one key per lever. Modelled on
  // bank-feed reconciliation — the pattern the brief tells us to copy, and one
  // that lives on the keyboard. Registered through the platform capability port
  // rather than `window`, so this page stays shell-agnostic.
  useEffect(() => {
    return capabilities.registerGlobalKeydownListener((event) => {
      // A single-letter shortcut that fires while someone is typing into the
      // search box is worse than no shortcut at all.
      if (pending || saving || event.hasModifier || event.fromTextEntry) return;

      if (event.key === "j" || event.key === "ArrowDown") {
        event.preventDefault();
        setFocusIndex((i) => Math.min(i + 1, navigable.length - 1));
        return;
      }
      if (event.key === "k" || event.key === "ArrowUp") {
        event.preventDefault();
        setFocusIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (event.key === "u") {
        event.preventDefault();
        undo();
        return;
      }
      if (event.key === "Escape") {
        setFocusIndex(-1);
        setSelected(new Set());
        return;
      }

      const row = navigable[focusIndex];
      if (!row) return;
      const base = {
        quoteId: row.quote.id,
        subjectLabel: row.quote.reference,
        subjectDetail: row.quote.description,
        monthLabel,
      };
      if (event.key === "l") {
        event.preventDefault();
        setPending({ ...base, lever: { kind: "status", target: "lost" } });
      } else if (event.key === "s") {
        event.preventDefault();
        setPending({
          ...base,
          lever: { kind: "status", target: "still_live" },
        });
      } else if (event.key === "c") {
        event.preventDefault();
        setPending({
          ...base,
          lever: { kind: "conversion" },
          currentShare: row.quote.conversion_share,
        });
      } else if (event.key === "d") {
        event.preventDefault();
        setPending({
          ...base,
          lever: { kind: "expected_date" },
          currentDate: row.quote.expected_conversion.date,
          derivedDate: row.quote.expected_conversion.derived_date,
          derivedMedianWd: row.quote.expected_conversion.derived_median_wd,
        });
      }
    });
  }, [capabilities, navigable, focusIndex, pending, saving, undo, monthLabel]);

  const focusedQuoteId = navigable[focusIndex]?.quote.id ?? null;

  const toggleSelect = useCallback((quoteId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(quoteId)) next.delete(quoteId);
      else next.add(quoteId);
      return next;
    });
  }, []);

  const toggleExpanded = useCallback((clientId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }, []);

  if (loading) {
    return (
      <PageFrame>
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg border border-border bg-surface-subtle"
            />
          ))}
        </div>
      </PageFrame>
    );
  }

  if (error || !data || !baseline) {
    return (
      <PageFrame>
        <Card className="p-6">
          <h2 className="font-semibold">Could not load your pipeline</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {error ?? "No data returned."}
          </p>
        </Card>
      </PageFrame>
    );
  }

  const pass = measureMove(
    baseline.projected_unbooked,
    data.projected_unbooked,
  );
  const startingFlags = baseline.flag_count;
  const cleared = startingFlags - data.flag_count;

  return (
    <PageFrame>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">My Pipeline</h1>
          <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {data.visible_reps.length > 1 ? (
              // A manager or admin switches books here, rather than being
              // sent round through Movement to find the link.
              <Select
                value={data.rep_id}
                onValueChange={(id) =>
                  navigate(
                    id === currentUser?.id
                      ? paths.myPipeline()
                      : paths.repPipeline({ repId: id }),
                  )
                }
              >
                <SelectTrigger className="h-8 w-56" aria-label="Whose book">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {data.visible_reps.map((rep) => (
                    <SelectItem key={rep.id} value={rep.id}>
                      {rep.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span>{data.rep_name}</span>
            )}
            <span className="inline-flex items-center gap-0.5">
              <span aria-hidden>·</span>
              {/* Step through the months the book is priced for: what the
                  rep expects to land in October, by client, is the same
                  arithmetic as this month, asked for a different month. */}
              <Button
                size="icon"
                variant="ghost"
                className="size-7"
                aria-label="Previous month"
                onClick={() => stepToMonth(previousMonthKey(data.month))}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <span className="min-w-28 text-center text-foreground">
                {data.month_label}
              </span>
              <Button
                size="icon"
                variant="ghost"
                className="size-7"
                aria-label="Next month"
                onClick={() => stepToMonth(data.next_month)}
              >
                <ChevronRight className="size-4" />
              </Button>
            </span>
            <span>· as at {formatShortDateOrDash(data.as_of_date)}</span>
          </p>
        </div>
        <Link
          to={
            repId
              ? `${paths.interventionLog()}?rep=${encodeURIComponent(repId)}`
              : paths.interventionLog()
          }
          className="text-sm text-primary hover:underline"
        >
          Intervention log
        </Link>
      </header>

      {data.since_last_visit && log.length === 0 && (
        <SinceLastVisitBar data={data} />
      )}

      {data.arrivals.length > 0 && (
        <ArrivalsBand count={data.arrivals.length} repId={repId} />
      )}

      <SummaryStrip data={data} pass={pass} />

      {startingFlags > 0 && (
        <PassProgress
          cleared={cleared}
          total={startingFlags}
          remaining={data.flag_count}
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <ViewTab
            active={view === "by_client"}
            onClick={() => setView("by_client")}
          >
            By client
          </ViewTab>
          <ViewTab
            active={view === "all_quotes"}
            onClick={() => setView("all_quotes")}
          >
            All quotes
          </ViewTab>
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a quote or client"
            className="h-8 w-56 pl-7 text-sm"
            aria-label="Find a quote or client"
          />
        </div>

        <ProvenanceLegend className="ml-auto" />
        <Button
          size="sm"
          variant={flaggedOnly ? "default" : "outline"}
          onClick={() => setFlaggedOnly((f) => !f)}
        >
          <ListFilter className="size-3.5" />
          {flaggedOnly ? "Flagged only" : "Show flagged only"}
        </Button>
        <Button
          size="sm"
          variant={hideLongDead ? "outline" : "default"}
          onClick={() => setHideLongDead((h) => !h)}
          aria-pressed={!hideLongDead}
        >
          <CalendarClock className="size-3.5" />
          {hideLongDead ? "Show older quotes" : "Older quotes shown"}
        </Button>
      </div>

      {selected.size > 0 && (
        <BulkBar
          count={selected.size}
          onClear={() => setSelected(new Set())}
          onMarkLost={() =>
            setPending({
              lever: { kind: "status", target: "lost" },
              subjectLabel: `${selected.size} quotes`,
              subjectDetail: "one reason applies to all",
              monthLabel: data.month_label,
              bulkCount: selected.size,
            })
          }
        />
      )}

      {view === "by_client" ? (
        visibleClients.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-2">
            {visibleClients.map((c) => (
              <ClientCompositionCard
                key={c.id}
                client={c}
                expanded={expanded.has(c.id)}
                monthLabel={data.month_label}
                query={query}
                hideLongDead={hideLongDead}
                selected={selected}
                focusedQuoteId={focusedQuoteId}
                onToggleSelect={toggleSelect}
                onToggle={() => toggleExpanded(c.id)}
                onIntervene={setPending}
              />
            ))}
          </div>
        )
      ) : (
        <AllQuotesTable
          clients={ordered}
          flaggedOnly={flaggedOnly}
          query={query}
          hideLongDead={hideLongDead}
          monthLabel={data.month_label}
          selected={selected}
          focusedQuoteId={focusedQuoteId}
          onToggleSelect={toggleSelect}
          onIntervene={setPending}
        />
      )}

      <KeyboardHint />

      <InterventionDialog
        request={pending}
        lostReasons={lostReasons}
        preview={previewFor}
        onApply={handleApply}
        onClose={() => setPending(null)}
      />

      {leverError && (
        <div
          role="alert"
          className="fixed inset-x-0 bottom-[max(1rem,env(safe-area-inset-bottom))] z-50 flex justify-center px-4"
        >
          <div className="border-destructive bg-card max-w-xl rounded-lg border px-4 py-2.5 text-sm shadow-lg">
            <span className="font-medium">Not saved.</span> {leverError}
          </div>
        </div>
      )}

      {!leverError && log.length > 0 && (
        <UndoToast entry={log[0]} count={log.length} onUndo={undo} />
      )}
    </PageFrame>
  );
}

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 p-4 pb-24">
      <WorkspaceNav active="pipeline" />
      {children}
    </div>
  );
}

/** What changed while the rep was away — what makes this a weekly rhythm. */
function SinceLastVisitBar({ data }: { data: MyPipelineResponse }) {
  const s = data.since_last_visit;
  if (!s) return null;
  const parts: string[] = [];
  if (s.new_quotes) parts.push(`${s.new_quotes} new quotes`);
  if (s.newly_flagged) parts.push(`${s.newly_flagged} aged into review`);
  if (s.converted) parts.push(`${s.converted} converted`);
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-border bg-surface-subtle px-4 py-2 text-sm">
      <Sparkles className="size-4 shrink-0 text-muted-foreground" />
      <span>
        Since{" "}
        {new Date(s.last_visited_at).toLocaleDateString("en-GB", {
          weekday: "long",
        })}
        : {parts.join(" · ")}.
      </span>
      <span className="text-muted-foreground">
        Your month has moved {s.projection_delta > 0 ? "+" : ""}
        {formatCurrencyGbpCompact(s.projection_delta)} since then.
      </span>
    </div>
  );
}

/**
 * The band sits above everything because an unsettled arrival is a live
 * double-count: the work and the prediction of it are both in the number below.
 */
function ArrivalsBand({ count, repId }: { count: number; repId?: string }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5">
      <div className="flex items-center gap-2 text-sm">
        <Inbox className="size-4 text-primary" />
        <span>
          <strong>
            {count} {count === 1 ? "arrival" : "arrivals"}
          </strong>{" "}
          to settle against projections you raised — counted twice until you do.
        </span>
      </div>
      <Button size="sm" asChild>
        <Link
          to={
            repId
              ? paths.repPipelineArrivals({ repId })
              : paths.myPipelineArrivals()
          }
        >
          Settle them
        </Link>
      </Button>
    </div>
  );
}

function SummaryStrip({
  data,
  pass,
}: {
  data: MyPipelineResponse;
  pass: InterventionImpact;
}) {
  const carriedPct = data.open_quoted_value
    ? Math.round((data.quotes_in_month / data.open_quoted_value) * 100)
    : 0;
  const thisMonth = data.month_label.split(" ")[0];
  const nextMonth = data.next_month_label.split(" ")[0];
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <div>
          <div className="text-2xl font-semibold tabular-nums">
            {formatCurrencyGbpCompact(data.projected_unbooked)}
          </div>
          <div className="text-xs text-muted-foreground">
            projected unbooked, {data.month_label}
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          <span className="tabular-nums text-foreground">
            {formatCurrencyGbpCompact(data.quotes_in_month)}
          </span>{" "}
          live quotes ·{" "}
          <span className="tabular-nums text-foreground">
            {formatCurrencyGbpCompact(data.unseen_in_month)}
          </span>{" "}
          projected unseen
        </div>
        {/* Next month, so work pushed out of this one visibly lands somewhere. */}
        <div className="border-l border-border pl-4 text-sm text-muted-foreground">
          <span className="tabular-nums text-foreground">
            {formatCurrencyGbpCompact(data.quotes_next_month)}
          </span>{" "}
          already expected in {nextMonth}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {data.flag_count > 0 ? (
            <Badge variant="warning">{data.flag_count} to review</Badge>
          ) : (
            <Badge variant="success">Book is clean</Badge>
          )}
        </div>
      </div>

      {pass.delta !== 0 && (
        <div
          className={`mt-3 rounded-md px-3 py-2 text-sm ${
            pass.isMaterial
              ? "bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
              : "bg-surface-subtle"
          }`}
        >
          Your pass has moved {thisMonth} by{" "}
          <strong className="tabular-nums">
            {pass.delta > 0 ? "+" : ""}
            {formatCurrencyGbpCompact(pass.delta)}
          </strong>{" "}
          ({Math.round(pass.deltaPct * 100)}%)
          {pass.isMaterial ? (
            <>
              {" "}
              — past the {Math.round(MATERIAL_MOVE_PCT * 100)}% threshold, so
              your sales manager sees this with your reasons attached.
            </>
          ) : (
            <>
              . Your manager is notified past{" "}
              {Math.round(MATERIAL_MOVE_PCT * 100)}%.
            </>
          )}
        </div>
      )}

      <p className="mt-3 border-t border-border pt-2.5 text-xs text-muted-foreground">
        Your open book is{" "}
        <strong className="text-foreground">
          {formatCurrencyGbpCompact(data.open_quoted_value)}
        </strong>
        , of which{" "}
        <strong className="text-foreground">
          {formatCurrencyGbpCompact(data.quotes_in_month)}
        </strong>{" "}
        ({carriedPct}%) is carried into {thisMonth}&rsquo;s forecast. The rest
        has either aged past the win curve or is expected to land in a later
        month.
      </p>
    </Card>
  );
}

/** Progress through the pass — the thing that lets a rep actually finish. */
function PassProgress({
  cleared,
  total,
  remaining,
}: {
  cleared: number;
  total: number;
  remaining: number;
}) {
  const pct = total > 0 ? Math.min(100, (cleared / total) * 100) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-muted-foreground">
          {remaining === 0 ? (
            <span className="font-medium text-green-700 dark:text-green-400">
              Pass complete — all {total} cleared. Tomorrow&rsquo;s snapshot
              carries your figures.
            </span>
          ) : (
            <>
              {cleared} of {total} cleared · {remaining} left
            </>
          )}
        </span>
        <span className="text-muted-foreground">{Math.round(pct)}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function BulkBar({
  count,
  onClear,
  onMarkLost,
}: {
  count: number;
  onClear: () => void;
  onMarkLost: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
      <span className="font-medium">{count} selected</span>
      <Button size="sm" onClick={onMarkLost}>
        Mark all lost
      </Button>
      <Button size="sm" variant="ghost" onClick={onClear}>
        Clear
      </Button>
      <span className="ml-auto text-xs text-muted-foreground">
        One reason applies to all of them.
      </span>
    </div>
  );
}

function ViewTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
        active
          ? "bg-primary/10 font-semibold text-primary"
          : "text-muted-foreground hover:bg-surface-hover hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function KeyboardHint() {
  return (
    <p className="text-center text-xs text-muted-foreground">
      <Kbd>j</Kbd> <Kbd>k</Kbd> move · <Kbd>l</Kbd> lost · <Kbd>s</Kbd> still
      live · <Kbd>c</Kbd> conversion · <Kbd>d</Kbd> date · <Kbd>u</Kbd> undo
    </p>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border border-border bg-surface-subtle px-1 py-0.5 font-mono text-[10px]">
      {children}
    </kbd>
  );
}

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed border-border px-4 py-12 text-center">
      <p className="font-medium">Nothing needs you.</p>
      <p className="mt-1 text-sm text-muted-foreground">
        Every client in your book has been reviewed and every quote has a
        decision. Tomorrow&rsquo;s snapshot carries your figures.
      </p>
    </div>
  );
}

/**
 * Undo. Interventions apply immediately with no approval step, which is the
 * brief's choice; this is what makes that safe rather than frightening.
 */
function UndoToast({
  entry,
  count,
  onUndo,
}: {
  entry: LogEntry;
  count: number;
  onUndo: () => void;
}) {
  return (
    <div className="fixed inset-x-0 bottom-[max(1rem,env(safe-area-inset-bottom))] z-40 flex justify-center px-4">
      <div className="flex max-w-xl items-center gap-3 rounded-lg border border-border bg-card px-4 py-2.5 shadow-lg">
        <span className="min-w-0 truncate text-sm">{entry.label}</span>
        {entry.reversible ? (
          <Button size="sm" variant="secondary" onClick={onUndo}>
            <Undo2 className="size-3.5" />
            Undo
          </Button>
        ) : (
          <span className="text-muted-foreground whitespace-nowrap text-xs">
            Saved
          </span>
        )}
        {count > 1 && (
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {count} this pass
          </span>
        )}
      </div>
    </div>
  );
}
